import type { RequiredStockLevelEntry } from "#/types/report";
import { Modal, NumberInput, SegmentedControl, Text } from "@mantine/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export type SimulationType = "increase" | "decrease";

interface StockSimulationModalProps {
  opened: boolean;
  onClose: () => void;
  /** Row the simulation runs against. Null while the modal is closed. */
  entry: RequiredStockLevelEntry | null;
}

/**
 * "What if demand moves?" for one category of the Required Stock card. Applies
 * a user-supplied percentage to that category's required stock and compares the
 * result against what the client actually holds today.
 */
const StockSimulationModal = ({ opened, onClose, entry }: StockSimulationModalProps) => {
  const { t } = useTranslation();
  const [type, setType] = useState<SimulationType>("increase");
  const [percent, setPercent] = useState<number | "">("");

  // Start each simulation from a clean slate.
  useEffect(() => {
    if (opened) {
      setType("increase");
      setPercent("");
    }
  }, [opened]);

  const required = entry?.requiredStock ?? null;
  const hasPercent = typeof percent === "number" && Number.isFinite(percent) && percent > 0;

  // Decrease can never take demand below zero.
  const factor =
    type === "increase"
      ? 1 + (typeof percent === "number" ? percent : 0) / 100
      : Math.max(0, 1 - (typeof percent === "number" ? percent : 0) / 100);

  const simulatedRequired =
    required !== null && hasPercent ? Math.ceil(required * factor) : null;

  // Positive = spare stock, negative = short.
  const difference =
    simulatedRequired !== null && entry ? entry.currentCount - simulatedRequired : null;

  const renderOutcome = () => {
    if (difference === null) return null;
    if (difference > 0) {
      return (
        <Text size="sm" className="text-emerald-600">
          {t("reports.inventoryScan.simulation.spare", { count: difference })}
        </Text>
      );
    }
    if (difference < 0) {
      return (
        <Text size="sm" className="text-red-600">
          {t("reports.inventoryScan.simulation.short", { count: Math.abs(difference) })}
        </Text>
      );
    }
    return (
      <Text size="sm" className="text-gray-600">
        {t("reports.inventoryScan.simulation.exact")}
      </Text>
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size="md"
      title={t("reports.inventoryScan.simulation.title")}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <Text size="sm" fw={600}>
            {entry?.productName ?? t("reports.inventoryScan.requiredStock.noCategory")}
          </Text>
          <Text size="xs" c="dimmed">
            {t("reports.inventoryScan.simulation.currentBaseline", {
              required: required ?? "--",
              current: entry?.currentCount ?? 0,
            })}
          </Text>
        </div>

        <div className="flex flex-col gap-1">
          <Text size="sm" fw={500}>
            {t("reports.inventoryScan.simulation.type")}
          </Text>
          <SegmentedControl
            value={type}
            onChange={(value) => {
              const next = value as SimulationType;
              setType(next);
              // A decrease past 100% is meaningless, so pull the value back in range.
              if (next === "decrease" && typeof percent === "number" && percent > 100) {
                setPercent(100);
              }
            }}
            data={[
              { label: t("reports.inventoryScan.simulation.increase"), value: "increase" },
              { label: t("reports.inventoryScan.simulation.decrease"), value: "decrease" },
            ]}
          />
          <Text size="xs" c="dimmed">
            {type === "increase"
              ? t("reports.inventoryScan.simulation.increaseHelp")
              : t("reports.inventoryScan.simulation.decreaseHelp")}
          </Text>
        </div>

        <NumberInput
          label={t("reports.inventoryScan.simulation.percent")}
          placeholder={t("reports.inventoryScan.simulation.percentPlaceholder")}
          value={percent}
          onChange={(value) => setPercent(value === "" ? "" : Number(value))}
          min={0}
          max={type === "decrease" ? 100 : 1000}
          clampBehavior="strict"
          allowNegative={false}
          suffix="%"
        />

        <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
          {simulatedRequired === null ? (
            <Text size="sm" c="dimmed">
              {t("reports.inventoryScan.simulation.enterPercent")}
            </Text>
          ) : (
            <div className="flex flex-col gap-1">
              <Text size="sm">
                {t("reports.inventoryScan.simulation.result", {
                  required: simulatedRequired,
                })}
              </Text>
              <Text size="xs" c="dimmed">
                {t("reports.inventoryScan.simulation.youHave", {
                  count: entry?.currentCount ?? 0,
                })}
              </Text>
              {renderOutcome()}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default StockSimulationModal;
